import type { Asset } from '@compass/client/types/asset';
import { assetService } from '@services/api';
import { useStore } from '@store/index';
import { useQuery } from '@tanstack/react-query';

/**
 * Fetches metadata (name, size, content type) for a single asset by ID.
 *
 * Used to display saved File-type field values, which store only the asset ID. Organization and
 * program come from the store. Cached with a long staleTime since asset metadata is immutable.
 */
export const useAsset = (assetId?: string, caseRecordId?: string) => {
  const organizationId = useStore(state => state.selectedOrganizationId);
  const programId = useStore(state => state.selectedProgramId);

  return useQuery<Asset | undefined>({
    queryKey: ['asset', organizationId, programId, caseRecordId, assetId],
    queryFn: async () => {
      const response = await assetService.getAsset({
        organization_id: organizationId!,
        program_id: programId!,
        case_record_id: caseRecordId!,
        asset_id: assetId!,
      });
      return response.asset;
    },
    enabled: !!assetId && !!caseRecordId && !!organizationId && !!programId,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });
};
